import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Icon, Txt } from '@vst/beam';
import { Check, ChevronDownCurved, LanguageOutlined } from '@vst/beam-icons/icons';
import Cookies from 'js-cookie';

import { supportedLocales } from '@app/services/translations';
import { useScreenResolution } from '@app/hooks';
import { selectLocale } from '@app/redux/locale';

import { useSwitchLanguage } from './hooks';

import styles from './styles.module.scss';

const SelectedItem = ({ name, testID }: { name: string; testID: string }) => {
  return (
    <div className={styles.selectedItem} data-cy={testID + '-selected'}>
      <Txt variant="smallRegular" data-cy={testID}>
        {name}
      </Txt>
      <Icon icon={Check} ml="8px" size={20} />
    </div>
  );
};

const LanguageDropdown = ({ handleToggle }: { handleToggle: () => void }) => {
  const switchLanguage = useSwitchLanguage();

  const {
    locale: { displayLanguage: currentDisplayLanguage, userLocale },
  } = useSelector(selectLocale);

  return (
    <div className={styles.dropdownContainer} data-cy="languageDropdown">
      {supportedLocales[userLocale].availableLanguages.map(({ displayLanguage, short, localized }) => {
        if (currentDisplayLanguage === displayLanguage) {
          return <SelectedItem testID={displayLanguage} name={localized} key={short} />;
        }

        return (
          <Txt
            key={displayLanguage}
            className={styles.item}
            variant="smallRegular"
            data-cy={displayLanguage}
            onClick={() => {
              switchLanguage(displayLanguage);
              handleToggle();
            }}
          >
            {localized}
          </Txt>
        );
      })}
    </div>
  );
};

const LanguageToggle = () => {
  const [isOpen, setIsOpen] = useState(false);
  const {
    locale: { userLocale },
  } = useSelector(selectLocale);
  const { isLarge, isExtraLarge } = useScreenResolution();
  const isMobile = !isLarge && !isExtraLarge;

  let chevronStyles = styles.chevron;
  if (isOpen) chevronStyles += ` ${styles.flip}`;

  const handleToggle = () => {
    setIsOpen((open) => !open);
  };

  function getSelectedDisplayLanguage() {
    const selectedLanguageEntry = supportedLocales[userLocale].availableLanguages.find(
      ({ displayLanguage }) => displayLanguage === Cookies.get('displayLanguage')
    );

    return selectedLanguageEntry ? selectedLanguageEntry.short : supportedLocales[userLocale].displayLanguage;
  }

  const selectedDisplayLanguage = getSelectedDisplayLanguage();

  return (
    <>
      {isOpen && <div className={styles.overlay} onClick={handleToggle} />}
      <div className={styles.container}>
        <Button variant="tertiary" className={styles.toggleContainer} onClick={handleToggle} data-cy="languageToggle">
          <Icon icon={LanguageOutlined} color={isMobile ? 'gray_700' : 'gray_500'} size={24} />
          {!isMobile && (
            <Txt variant="bodySmallRegular" color="subtle" className={styles.languageText} data-cy="currentLanguage">
              {selectedDisplayLanguage}
            </Txt>
          )}
          <Icon
            icon={ChevronDownCurved}
            size={26}
            color={isMobile ? 'gray_700' : 'gray_500'}
            className={chevronStyles}
          />
        </Button>
        {isOpen && <LanguageDropdown handleToggle={handleToggle} />}
      </div>
    </>
  );
};

export default LanguageToggle;
